import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { formatPhone } from "commonAssets/lib/formatPhone";
import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import formDefault from "../../docs/formDefault.json";

/**
 * Prepends a label to each `tel` field wrapper in a rendered claim-fields
 * container. Mirrors the common FormFieldsLabel markup (`<label for=...>` with
 * the required star and error span) so `tel` fields are labeled site-side
 * without modifying shared assets.
 *
 * @param {HTMLElement} container - The container returned by FormBase.claim_fields.
 * @param {Array<Object>} fields - The claim_fields definitions from the schema.
 */
function appendTelLabels(container, fields) {
    if (!Array.isArray(fields)) {
        return;
    }
    fields.forEach((field) => {
        if (field.type !== "tel") {
            return;
        }
        const wrapper = container.querySelector(
            `[data-field-id="_${field.name}"]`
        );
        if (!wrapper) {
            return;
        }
        const fieldLabel = i18n.getField(field.label);
        const star = field.required ? "*" : "";
        const label = createElement(
            "label",
            { for: field.name },
            [],
            `${fieldLabel}${star} <span class="error"></span>`
        );
        wrapper.prepend(label);
    });
}

/**
 * Site-scoped SubmitClaim for www.sxmtcpasettlement.com — Fix Claim
 * (deficiency) portal.
 *
 * This is a correction-only portal: only existing claimants whose appDocket
 * `cm_data.ClassMemberStatus[]` carries an Active `Deficient` status with
 * `CanEdit === true` may proceed to edit their claim. Generic/new filing is
 * disabled via `formDefault.json` (`generic_claim.display: false`).
 *
 * `init_claim` is a literal copy of the shared `SubmitClaim.init_claim` with a
 * single deficiency gate inserted in the guard's `classMemberPost` success
 * block (and the standard guard deadline alert removed). Keeping the rest 1:1
 * with the base minimizes drift and preserves the OTP / populate / submit flow.
 */
export class customSubmitClaim extends SubmitClaim {
    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    // Deficiency portal: remove the standard claim-deadline
                    // alert from the guard (the close date is conveyed
                    // elsewhere for the Fix Claim flow).
                    const guardAlert =
                        document.getElementById("claim_form_alert");
                    if (guardAlert) {
                        guardAlert.remove();
                    }

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    // START DEFICIENCY GATING
                                    // Correction-only portal: only an Active
                                    // "Deficient" status with CanEdit may
                                    // proceed. Fail closed if cm_data is
                                    // missing.
                                    let cmLabel = false;
                                    let cmActive = false;
                                    let cmCanEdit = false;
                                    let cmReason = "";

                                    // ClassMemberStatus is nested under
                                    // class_member when a claim_id exists,
                                    // otherwise it sits directly on result.data.
                                    const ClassMemberStatus = Boolean(
                                        result.data.claim_id
                                    )
                                        ? result.data.class_member?.cm_data
                                              ?.ClassMemberStatus
                                        : result.data.cm_data?.ClassMemberStatus;

                                    if (!Array.isArray(ClassMemberStatus)) {
                                        modal(
                                            "Error",
                                            "This portal is not available for new claims."
                                        );
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    for (const status of ClassMemberStatus) {
                                        if (status.Label !== "Deficient")
                                            continue;
                                        cmLabel = true;
                                        cmActive = Boolean(status.Active);
                                        cmCanEdit = Boolean(status.CanEdit);
                                        cmReason = status.Reason || "";
                                        break;
                                    }

                                    if (!cmLabel || !cmActive || !cmCanEdit) {
                                        modal(
                                            "Error",
                                            "This portal is not available for claim edits."
                                        );
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    // Persist the deficiency reason so it can be
                                    // surfaced on the populated Fix a Claim form.
                                    this.claim.data.deficiency_reason = cmReason;
                                    // Persist the payment-on-file flag (top-level
                                    // on result.data, or nested under class_member
                                    // on the claim_id shape) so customLoadMember can
                                    // seed the on-file sentinel before dstMember.
                                    this.claim.data.has_payment_info = Boolean(
                                        result.data.class_member
                                            ?.has_payment_info ??
                                            result.data.has_payment_info
                                    );
                                    // END DEFICIENCY GATING

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    /**
     * Site-scoped override of FormBase.claim_fields.
     *
     * The shared FormFieldsLabel intentionally omits `tel` fields, so the base
     * implementation renders the `telephone_number` input without a label.
     * This site requires a label, so we render the base fields and prepend the
     * label for any `tel` field, mirroring the common FormFieldsLabel markup.
     */
    claim_fields(params) {
        const container = super.claim_fields(params);
        appendTelLabels(container, params.claim_fields);
        return container;
    }

    /**
     * Deficiency portal: once the existing claim is populated for editing,
     * surface the claimant's deficiency reason + cure instructions and enable
     * adding extra phone numbers to cure the deficiency. All deficient paths
     * (direct populate and post-OTP) funnel through populate_claim_form.
     */
    populate_claim_form(form, container, class_member, claim_deadline) {
        super.populate_claim_form(form, container, class_member, claim_deadline);
        this.render_deficiency_notice(form);
        this.setup_additional_phones(form, class_member);
    }

    /**
     * Site-scoped override of FormBase.destroy_claim.
     *
     * We don't normally email a confirmation on claim edits — only new claims.
     * Ops (WP-1415) wants deficiency re-submissions (an edit: `claim_id` present)
     * to receive one too. `destroy_claim` runs from `post_claim`'s success
     * `.finally`, i.e. after uploads and after the `claimsPut` save resolves, so
     * this is the correct point to send. We defer the confirmation UI to `super`
     * then fire `claimsEmailPost` fire-and-forget, gated on a successful save and
     * an existing claim. Payload shape mirrors the repo's other callers
     * (gsg/aligner): `{ domain, zero_zero_id, confirmation_code, claim_id }`.
     */
    destroy_claim(claim) {
        super.destroy_claim(claim);
        if (claim.flags.success && claim.data.claim_id) {
            claim.api
                .claimsEmailPost(
                    {},
                    {
                        domain: claim.data.domain,
                        zero_zero_id: claim.data.zero_zero_id,
                        confirmation_code: claim.data.confirmation_code,
                        claim_id: claim.data.claim_id,
                    }
                )
                .catch((error) => {
                    // Non-blocking: the confirmation UI has already rendered via
                    // super. A failed send must not throw on the claimant's
                    // screen; log to Sentry so failures stay visible.
                    this.errorHandler.log_exception(
                        new Error(
                            "Error sending deficiency confirmation email",
                            { cause: error }
                        )
                    );
                });
        }
    }

    /**
     * Renders the dynamic deficiency reason (from cm_data) followed by the
     * static cure-instructions copy in a red alert at the top of the form. The
     * reason is set via textContent (data-sourced); the cure copy is trusted
     * site config.
     */
    render_deficiency_notice(form) {
        const formEl = document.getElementById("form");
        if (!formEl || document.getElementById("deficiency-notice")) {
            return;
        }
        const cureBody = form.form_options.deficiency
            ? form.form_options.deficiency.cure_body
            : "";
        const notice = createElement(
            "div",
            {
                class: "alert alert-danger fw-bold",
                id: "deficiency-notice",
                role: "alert",
            },
            [],
            cureBody
        );
        const reason = this.claim.data.deficiency_reason;
        if (reason) {
            const reasonEl = createElement("p", { class: "mb-2" });
            reasonEl.textContent = reason;
            notice.insertBefore(reasonEl, notice.firstChild);
        }
        formEl.insertBefore(notice, formEl.firstChild);
    }

    /**
     * Adds an "Add another phone number" control to the telephone section so a
     * claimant can supply multiple numbers, and re-renders any previously saved
     * additional numbers when an existing claim is opened for editing. Added
     * inputs use distinct names so every value is captured in the claim
     * payload; empty added fields are optional (validated only when filled) and
     * can be removed.
     *
     * @param {Object} form - The form schema (claim_form[lang]).
     * @param {Object} [class_member] - The populated claim record; its
     *   `claim_fields` are scanned to restore saved "Telephone Number n" values.
     */
    setup_additional_phones(form, class_member) {
        const claimFields = document.getElementById("claim-fields");
        if (!claimFields || document.getElementById("add-phone-number")) {
            return;
        }
        const baseWrapper = claimFields.querySelector(
            '[data-field-id="_telephone_number"]'
        );
        if (!baseWrapper) {
            return;
        }

        const deficiency = form.form_options.deficiency || {};
        const addLabel =
            deficiency.add_phone_label || "Add another phone number";
        const removeLabel = deficiency.remove_phone_label || "Remove";
        let phoneCount = 1;

        const addBtn = createElement(
            "button",
            {
                type: "button",
                class: "btn btn-primary btn-sm mb-3",
                id: "add-phone-number",
            },
            [],
            addLabel
        );

        // Builds one additional phone field (optionally prefilled) and inserts
        // it before the add button. Shared by manual adds and re-login restore.
        const addPhoneField = (index, value) => {
            // Machine-safe id/name (valid selector); data-label is the claim
            // field key Ops expects ("Telephone Number 2", "Telephone Number 3").
            const fieldId = `telephone_number_${index}`;
            const dataLabel = `Telephone Number ${index}`;
            const label = createElement(
                "label",
                { for: fieldId },
                [],
                'Telephone Number <span class="error"></span>'
            );
            const input = createElement(
                "input",
                {
                    type: "tel",
                    class: "form-control",
                    id: fieldId,
                    name: fieldId,
                    "data-label": dataLabel,
                    "data-validate": "phone",
                },
                [],
                "",
                { keyup: formatPhone }
            );
            if (value) {
                input.value = value;
            }
            const wrapper = createElement("div", {
                class: "mb-3 _wrapper additional-phone",
                "data-field-id": `_${fieldId}`,
            });
            const removeBtn = createElement(
                "button",
                {
                    type: "button",
                    class: "btn btn-link btn-sm text-danger p-0",
                },
                [],
                removeLabel,
                { click: () => wrapper.remove() }
            );
            wrapper.append(label, input, removeBtn);
            addBtn.parentNode.insertBefore(wrapper, addBtn);
        };

        addBtn.addEventListener("click", () => {
            phoneCount++;
            addPhoneField(phoneCount, "");
        });

        baseWrapper.parentNode.insertBefore(addBtn, baseWrapper.nextSibling);

        // Re-login restore: re-render previously saved additional phone numbers
        // (keyed "Telephone Number 2".."Telephone Number n") prefilled.
        // loadMember skips them because the matching fields do not exist in the
        // DOM at populate time.
        const savedFields = (class_member && class_member.claim_fields) || [];
        savedFields
            .map((field) => ({
                field,
                match: /^Telephone Number (\d+)$/.exec(field.name),
            }))
            .filter((entry) => entry.match)
            .sort((a, b) => Number(a.match[1]) - Number(b.match[1]))
            .forEach((entry) => {
                const index = Number(entry.match[1]);
                if (index > phoneCount) {
                    phoneCount = index;
                }
                addPhoneField(index, entry.field.value);
            });
    }
}
